import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { EventPublisherService } from '@bootstrap/services/event-publisher.service';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { MaterialQuery } from '@modules/material/state/material-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { getToothNumberById } from '@modules/teeth-diagram/models/teeth-numbering';
import { TeethNumberingSystem } from '@modules/teeth-diagram/models/teeth-numbering-system.enum';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { ToothEditorUpdatingService } from '@modules/tooth-editor/services/tooth-editor-updating.service';
import { TranslateService } from '@ngx-translate/core';
import { IBaseFacade } from '@shared/base-classes/base.facade';
import { AbutmentType } from '@shared/models/abutment-type';
import { AbutmentTypes } from '@shared/models/abutment-type.enum';
import { DropDownItem } from '@shared/models/drop-down-item';
import { IdName } from '@shared/models/id-name';
import { ImplantMasterList } from '@shared/models/implant-master-list';
import { LimitedFeatures } from '@shared/models/limited-features';
import { Material } from '@shared/models/material';
import { MaterialOption } from '@shared/models/material-option';
import { ProcedureMap } from '@shared/models/procedure-map';
import { RoleTypeEnum } from '@shared/models/role-type';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { SpecIdWithMaterialIds } from '@shared/models/rx-rules-json-interface';
import { ShadeSystem } from '@shared/models/shade-system';
import { BridgeValidationState, BridgeValidatorService } from '@shared/services/bridge-validator.service';
import { BridgeService } from '@shared/services/bridge.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ImplantsViewService } from '@shared/implants-view/implants-view.service';
import { RxIndicationRulesService } from '@shared/services/rx-rules-helper/rx-indication-rules.service';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { RxRulesService } from '@shared/services/rx-rules-helper/rx-rules.service';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { getSortedListByName } from '@shared/services/sort-list-by-name-util';
import { VersionsService } from '@shared/services/versions.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { ImplantsViewAppParameter } from '../../shared/implants-view/models/implants-view-app-parameter';
import { AbutmentData, CrownData, ImplantsViewData, ToothEditorData, ToothEditorState } from './models/tooth-editor';
import { ToothEditorQuery } from './state/tooth-editor-query';
import { ToothEditorStore } from './state/tooth-editor-store';
import { Specification } from '@shared/models/specification';

@Injectable()
export class ToothEditorFacade implements IBaseFacade {
	constructor(
		private readonly translateService: TranslateService,
		private readonly toothEditorQuery: ToothEditorQuery,
		private readonly toothEditorStore: ToothEditorStore,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly materialQuery: MaterialQuery,
		private readonly rxIndicationRulesService: RxIndicationRulesService,
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly rxRulesService: RxRulesService,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly bridgeService: BridgeService,
		private readonly versionService: VersionsService,
		private readonly updatingService: ToothEditorUpdatingService,
		private readonly bridgeValidatorService: BridgeValidatorService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly eventPublishService: EventPublisherService,
		private readonly implantsViewService: ImplantsViewService
	) {}
	getSortedListByName = getSortedListByName;

	isRxSent$: Observable<boolean> = this.shellQuery.isRxSent$;
	getState$: Observable<ToothEditorState> = this.toothEditorQuery.toothEditorState$;
	teeth$: Observable<Tooth[]> = this.toothEditorQuery.teeth$;
	teethNumberingSystem$: Observable<TeethNumberingSystem> = this.teethDiagramQuery.teethNumberingSystem$;
	isBridge$: Observable<boolean> = this.toothEditorQuery.isBridge$;
	allTeethInJaw$: Observable<Tooth[]> = this.toothEditorQuery.allTeethInJaw$;
	toothClickedOn$: Observable<Tooth | null> = this.toothEditorQuery.toothClickedOn$;
	implantMasterList$: Observable<Maybe<ImplantMasterList>> = this.shellQuery.implantMasterList$;
	isReadOnly$: Observable<boolean> = combineLatest([
		this.shellQuery.getReadOnlyState('toothEditor'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isHostPlatformIteroLab]) => isReadOnly || isHostPlatformIteroLab));
	shouldShowNewImplantsView$: Observable<boolean> = combineLatest([this.shellQuery.rxConfiguration$, this.shellQuery.teeth$]).pipe(
		filter(([rxConfiguration]) => !!rxConfiguration),
		map(([rxConfiguration, teeth]) =>
			this.implantsViewService.shouldShowNewImplantsView(
				rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany ?? [],
				teeth ?? []
			)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	abutmentTypes: AbutmentType[];
	abutmentTypes$: Observable<AbutmentType[]> = this.rxRulesService.abutmentTypes$.pipe(
		tap(abutmentTypes => (this.abutmentTypes = abutmentTypes))
	);
	clientVersion$: Observable<Maybe<string>> = this.shellQuery.clientVersion$;
	highestPackageVersion$: Observable<Maybe<string>> = this.shellQuery.highestPackageVersion$;

	crownSectionMaterialOptions$: Observable<IdName[]> = combineLatest([
		this.shellQuery.rxConfiguration$,
		this.orderQuery.caseType$,
		this.toothClickedOn$
	]).pipe(
		map(([rxConfig, caseTypeItem, toothClickedOn]: [RxConfiguration, IdName, Tooth]) => {
			const materialNames: Material[] = rxConfig ? [...rxConfig.Materials] : [];
			const materialIds = this.rxRulesService.getMaterialIds(
				caseTypeItem?.Id,
				toothClickedOn.ToothID as any,
				toothClickedOn.UnitTypeID as any
			);

			return this.rxMaterialRulesService.getFilteredMaterials(materialIds, materialNames);
		})
	);

	materialsByDefault$: Observable<Material[]> = combineLatest([
		this.toothClickedOn$,
		this.shellQuery.rxConfiguration$,
		this.orderQuery.caseType$,
		this.shellQuery.isNewAndNotClonedRx$
	]).pipe(
		map(([toothClickedOn, rxConfig, caseTypeItem, isNewRx]: [Tooth, RxConfiguration, IdName, boolean]) => {
			const isBridgeTooth = !toothClickedOn?.UnitTypeID;
			const unitTypeConverted = isBridgeTooth
				? this.convertFromBridgeToothUnitType({ unitTypeInBridge: toothClickedOn?.ToothInBridgeTypeID as any })
				: toothClickedOn?.UnitTypeID;
			const materialNames: Material[] = rxConfig
				? [...rxConfig.Materials].filter((material: Material) => (isNewRx ? material.IsActive : true))
				: [];
			const materialIds = this.rxRulesService.getMaterialIds(
				caseTypeItem?.Id,
				toothClickedOn?.ToothID as any,
				unitTypeConverted as any
			);

			return this.rxMaterialRulesService.getFilteredMaterials(materialIds, materialNames);
		})
	);

	allMaterialNames: IdName[];
	allMaterialNames$: Observable<Material[]> = combineLatest([
		this.shellQuery.rxConfiguration$,
		this.shellQuery.isNewAndNotClonedRx$
	]).pipe(
		map(([rxConfig, isNewRx]: [RxConfiguration, boolean]) => {
			const materialNames: Material[] = rxConfig
				? [...rxConfig.Materials].filter((material: Material) => (isNewRx ? material.IsActive : true))
				: [];
			const materialNameOptions = materialNames?.map(materialName => ({ Id: materialName.Id, Name: materialName.Name }));

			this.allMaterialNames = this.getSortedListByName(materialNameOptions);

			return materialNames;
		})
	);

	allAbutmentTypeOptions: IdName[];
	allAbutmentTypeOptions$: Observable<IdName[]> = this.abutmentTypes$
		.pipe(map(abutmentTypes => abutmentTypes.map(abutmentType => ({ Id: abutmentType.Id, Name: abutmentType.Name }))))
		.pipe(tap(allAbutmentTypeOptions => (this.allAbutmentTypeOptions = allAbutmentTypeOptions)));

	abutmentTypeOptions: IdName[];
	/* @TODO: will use HighestPackageVersions$ instead of HighestPackageVersion$ when ScrewRetainCrown requirements are elaborated */
	abutmentTypeOptions$: Observable<IdName[]> = combineLatest([
		this.abutmentTypes$,
		this.clientVersion$,
		this.shellQuery.packageVersion$,
		this.highestPackageVersion$,
		this.hostPlatformService.isScanner$
	])
		.pipe(
			map(
				([abutmentTypes, clientVersion, packageVersion, highestPackageVersion, isHostPlatformScanner]: [
					AbutmentType[],
					string,
					string,
					string,
					boolean
				]) =>
					abutmentTypes
						.filter((abutmentType: AbutmentType) => {
							packageVersion = isHostPlatformScanner ? packageVersion : highestPackageVersion;

							return (
								abutmentType.Id !== AbutmentTypes.ScrewRetainedCrown ||
								(abutmentType.Id === AbutmentTypes.ScrewRetainedCrown &&
									this.versionService.isFeatureAvailableForScanner(
										LimitedFeatures.ScrewRetainCrown,
										clientVersion,
										packageVersion
									))
							);
						})
						?.map((abutmentType: AbutmentType): IdName => ({ Id: abutmentType.Id, Name: abutmentType.Name }))
			)
		)
		.pipe(tap(abutmentTypeOptions => (this.abutmentTypeOptions = this.getSortedListByName(abutmentTypeOptions))));

	copyFromToothOptions$: Observable<IdName[]> = combineLatest([
		this.teethDiagramQuery.teeth$,
		this.teethDiagramQuery.teethNumberingSystem$
	]).pipe(
		map(([teeth, teethNumberingSystem]) => {
			const jaws: Tooth[] = [...teeth.upperJaw, ...teeth.lowerJaw];
			const allPreps = jaws.filter(
				tooth => this.isPrep({ tooth }) && this.toothClickedOn?.ToothID && this.toothClickedOn.ToothID !== tooth.ToothID
			);

			return allPreps.map(prepTooth => ({
				Id: prepTooth.ToothID,
				Name: `${getToothNumberById({ toothId: prepTooth.ToothID as any, teethNumberingSystem })} (${this.getUnitTypeTranslation({
					tooth: prepTooth,
					isBridgeContext: !!this.toothClickedOn?.BridgeIndex
				})})`
			}));
		})
	);

	shouldValidateForSend$: Observable<boolean> = this.shellQuery.shouldValidateForSend$;

	implantsViewAppParameters$: Observable<ImplantsViewAppParameter> = combineLatest([
		this.toothClickedOn$,
		this.shouldValidateForSend$,
		this.isReadOnly$,
		this.isBridge$,
		// attachmentsRxId$ resolves rxId even for new Rx (falls back to preparedNewRxId), unlike rxId$ which stays null until saved
		this.shellQuery.attachmentsRxId$
	]).pipe(
		tap(([tooth, shouldValidateForSend, isReadOnly, isBridge]): Observable<[Tooth, boolean, boolean]> => {
			if (isBridge) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.eventPublishService.publishRxChangeImplantsViewAppParameters({
					toothNumber: tooth?.ToothID as any,
					preselectedImplantManufacturerId: tooth?.ImplantsViewData?.ImplantManufacturerId,
					preselectedImplantConnectionId: tooth?.ImplantsViewData?.ImplantConnectionId,
					preselectedImplantPlatformId: tooth?.ImplantsViewData?.ImplantPlatformId,
					preselectedScanBodyId: tooth?.ImplantsViewData?.ScanBodyId
				});
			}

			return of([tooth as Tooth, shouldValidateForSend, isReadOnly]);
		}),
		map(([tooth, shouldValidateForSend, isReadOnly, , rxId]) => {
			const appParameters: ImplantsViewAppParameter = {
				toothNumber: tooth?.ToothID as any,
				preselectedImplantManufacturerId: tooth?.ImplantsViewData?.ImplantManufacturerId,
				preselectedImplantConnectionId: tooth?.ImplantsViewData?.ImplantConnectionId,
				preselectedImplantPlatformId: tooth?.ImplantsViewData?.ImplantPlatformId,
				preselectedScanBodyId: tooth?.ImplantsViewData?.ScanBodyId,
				shouldValidateForSend,
				isReadOnly,
				...(rxId && { rxId })
			};

			return appParameters;
		})
	);

	userRole$: Observable<RoleTypeEnum> = this.shellQuery.userRole$;

	get isProcedureFlow(): boolean {
		return this.shellQuery.isProcedureFlow;
	}

	get procedureMap(): ProcedureMap | null {
		return this.orderQuery.procedureMap;
	}

	get toothClickedOn(): Tooth | null {
		return this.toothEditorQuery.toothClickedOn;
	}

	get teeth(): Tooth[] {
		return this.toothEditorQuery.teeth;
	}

	get isToothInBridge(): boolean {
		return !!this.toothClickedOn?.BridgeIndex;
	}

	get isReadOnlyBannerVisible(): Observable<boolean> {
		return combineLatest([this.isReadOnly$, this.isRxSent$, this.hostPlatformService.isScanner$]).pipe(
			map(([isReadOnly, isRxSent, isHostPlatformScanner]) => {
				return !isHostPlatformScanner && isReadOnly && isRxSent;
			})
		);
	}

	get ponticDesigns(): Maybe<IdName[]> {
		return this.shellQuery.ponticDesigns;
	}

	get isGlidewellOrder(): boolean {
		return this.orderQuery.isGlidewellOrder;
	}

	getMaterialsBySpecification(specId: number, specIdsWithMaterialIds: SpecIdWithMaterialIds[]): Material[] {
		const ids = this.rxMaterialRulesService.getMaterialIdsBySpecification(specId, specIdsWithMaterialIds);

		return this.rxMaterialRulesService.getFilteredMaterials(ids);
	}

	getSpecIdsWithMaterialIds(pmToCheck: Maybe<ProcedureMapCheckedValue>, currentUnitTypeOnTooth: UnitTypes): SpecIdWithMaterialIds[] {
		return this.rxMaterialRulesService.getSpecIdsWithMaterialIds(pmToCheck, currentUnitTypeOnTooth);
	}

	getSpecIdsWithMaterialIdsForBridge(
		pmToCheck: Maybe<ProcedureMapCheckedValue>,
		bridgeUnitTypeOnTooth: UnitTypesInBridge
	): SpecIdWithMaterialIds[] {
		return this.rxMaterialRulesService.getSpecIdsWithMaterialIdsForBridge(pmToCheck, bridgeUnitTypeOnTooth);
	}

	convertToBridgeToothUnitType({ unitType }: { unitType: UnitTypes }): UnitTypesInBridge | null {
		return this.bridgeService.convertToBridgeToothUnitType({ unitType });
	}

	convertFromBridgeToothUnitType({ unitTypeInBridge }: { unitTypeInBridge: UnitTypesInBridge }): UnitTypes | null {
		return this.bridgeService.convertFromBridgeToothUnitType({ unitTypeInBridge });
	}
	getPreparationDesignOptionsMapping(): IdName[] {
		return this.rxRulesService.getPreparationDesignOptionsMapping();
	}

	getStumpShadeOptions(): IdName[] {
		return this.rxShadeSystemRulesService.getStumpShadeOptions() ?? [];
	}

	getMarginDesignOptions(): IdName[] {
		return this.rxRulesService.getMarginDesignOptions();
	}

	getMaterialOption(materialId: number): MaterialOption | undefined {
		return this.isProcedureFlow ? this.getMaterialSpecOption(materialId) : this.getMaterialUnitTypeOption(materialId);
	}

	getFilteredSpecifications(specIds: (number | string)[], specifications?: Specification[]): Specification[] {
		return this.rxMaterialRulesService.getFilteredSpecifications(specIds, specifications);
	}

	getMaterialSpecOption(materialId: number): MaterialOption | undefined {
		// material group is needed for custom materials when they weren't loaded
		return this.rxMaterialRulesService.getMaterialSpecOption(
			materialId,
			this.toothClickedOn?.SpecificationId as any,
			this.toothClickedOn?.CustomLabMaterialGroup
		);
	}

	getMaterialUnitTypeOption(materialId: number): MaterialOption | undefined {
		const isBridgeTooth = !this.toothClickedOn?.UnitTypeID;
		const convertedFromBridgeUnitType = this.convertFromBridgeToothUnitType({
			unitTypeInBridge: this.toothClickedOn?.ToothInBridgeTypeID as any
		});
		const unitTypeId = isBridgeTooth ? convertedFromBridgeUnitType : this.toothClickedOn?.UnitTypeID;

		return this.rxMaterialRulesService.getMaterialUnitTypeOption({ materialId, unitTypeId: unitTypeId as any });
	}

	setFormGroupReadOnlyMode(formGroup: UntypedFormGroup): void {
		Object.values(formGroup.controls).map(control => {
			control.disable();
		});
	}

	updateToothClickedOn({ toothClickedOn }: { toothClickedOn: Tooth }) {
		this.toothEditorStore.updateToothClickedOn({ toothClickedOn });
	}

	updateIsBridge({ isBridge }: { isBridge: boolean }) {
		this.toothEditorStore.updateIsBridge({ isBridge });
	}

	updateAllTeethInJaw({ allTeethInJaw }: { allTeethInJaw: Tooth[] }) {
		this.toothEditorStore.updateAllTeethInJaw({ allTeethInJaw });
	}

	getAvailableBridgeSpan({ toothClickedOn, allTeethInJaw }: { toothClickedOn: Tooth; allTeethInJaw: Tooth[] }) {
		return this.bridgeService.getAvailableBridgeSpan({ toothClickedOn, allTeethInJaw });
	}

	getAbutmentMarginStyleName({ marginDesignId }: { marginDesignId: string }): string | undefined {
		const abutmentMarginDesigns: IdName[] = this.getAbutmentOptions();
		const abutmentMarginStyleName = abutmentMarginDesigns?.find(
			abutmentMarginDesignOption => abutmentMarginDesignOption.Id === marginDesignId
		)?.Name;

		return abutmentMarginStyleName ? this.translateService.instant(`MarginStyles.${abutmentMarginStyleName}`) : undefined;
	}

	initTeeth({ teeth }: { teeth: Tooth[] }): void {
		this.toothEditorStore.updateTeeth({ teeth });
	}

	selectNearestToothInBridge(teeth: Tooth[]): void {
		// @ts-expect-error - TODO: fix this type error after updating Tooth model
		const firstToSelected = teeth[0].ToothID - this.toothClickedOn?.ToothID;
		// @ts-expect-error - TODO: fix this type error after updating Tooth model
		const selectedToLast = this.toothClickedOn?.ToothID - teeth[teeth.length - 1].ToothID;

		if (firstToSelected * selectedToLast >= 0) {
			return;
		}

		if (Math.abs(firstToSelected) < Math.abs(selectedToLast)) {
			this.updateToothClickedOn({ toothClickedOn: teeth[0] });
		} else {
			this.updateToothClickedOn({ toothClickedOn: teeth[teeth.length - 1] });
		}
	}

	updateTooth({ tooth, crownData, abutmentData, implantTypeId, implantsViewData }: ToothEditorData): void {
		const updatedTooth = this.getUpdatedTooth({ tooth, crownData, abutmentData, implantTypeId, implantsViewData });

		this.updatingService.updateSelectedTooth(updatedTooth);
	}

	updateToothAndSharedFieldsInBridge({ tooth, crownData, abutmentData, implantTypeId }: ToothEditorData): void {
		const updatedTooth = this.getUpdatedTooth({ tooth, crownData, abutmentData, implantTypeId });

		this.updatingService.updateSelectedToothAndSharedFieldsInBridge(updatedTooth);
	}

	getStateSync(property?: string) {
		return property ? this.toothEditorQuery.getValue()[property as keyof ToothEditorState] : this.toothEditorQuery.getValue();
	}

	getUnitTypeNames(tooth: Tooth): Observable<IdName[]> {
		if (this.isProcedureFlow) {
			return this.getProcedureFlowUnitTypeNames(tooth);
		}

		return this.getCaseTypeFlowUnitTypeNames(tooth);
	}

	getUnitTypeNamesForBridge(tooth: Tooth): Observable<IdName[]> {
		if (this.isProcedureFlow) {
			return this.getProcedureFlowUnitTypeNamesForBridge(tooth);
		}

		return this.getCaseTypeFlowUnitTypeNamesForBridge(tooth);
	}

	getCaseTypeFlowUnitTypeNamesForBridge(tooth: Tooth): Observable<IdName[]> {
		return this.teeth$.pipe(
			take(1),
			map((teethInBridge: Tooth[]) => {
				const isOuterTooth = this.bridgeService.isOuterTooth(tooth, teethInBridge);

				if (this.isGlidewellOrder) {
					return this.rxRulesService.getCaseTypeFlowUnitTypeNamesForGlidewellBridge(isOuterTooth);
				}

				return this.rxRulesService.getCaseTypeFlowUnitTypeNamesForBridge(isOuterTooth);
			})
		);
	}

	mapPreparationDesignOptions(prepDesignValues: string[]): IdName[] {
		return prepDesignValues?.map((prepDesignId: string) => this.getPreparationDesignOption(prepDesignId));
	}

	getPreparationDesignOption(preparationDesignId: string): IdName {
		return {
			Id: preparationDesignId,
			Name: this.getPreparationDesignName({ preparationDesignId }) ?? ''
		};
	}

	getDefaultShadeSystemFromUserSettings = ({
		defaultShadeSystemOptions
	}: {
		defaultShadeSystemOptions: ShadeSystem[];
	}): ShadeSystem | undefined => {
		const shadeSystemId = this.shellQuery.userSettings?.ShadeSystemId;

		return defaultShadeSystemOptions?.find(option => option.Id === shadeSystemId);
	};

	getShadeSystemOptionsAndSelectionForV1(shadeSystemIdFromTooth: number): { shadeSystem: ShadeSystem; options: ShadeSystem[] } {
		const defaultShadeSystemOptions = this.getDefaultShadeSystemOptions();

		const userSettingsShadeSystemOption = this.getDefaultShadeSystemFromUserSettings({
			defaultShadeSystemOptions
		});
		const shadeSystemSelection = defaultShadeSystemOptions.find(option => option.Id === shadeSystemIdFromTooth);

		return {
			shadeSystem: shadeSystemSelection || userSettingsShadeSystemOption || defaultShadeSystemOptions[0],
			options: defaultShadeSystemOptions
		};
	}

	getShadeSystemOptionsByMaterialId = ({ materialId }: { materialId: number }) => {
		return this.rxShadeSystemRulesService.getShadeSystemOptionsByMaterialId({ materialId });
	};

	getDefaultShadeSystemOptions(): ShadeSystem[] {
		return this.rxShadeSystemRulesService.getDefaultShadeSystemOptions();
	}

	getDefaultShadeSystemSelection = ({ materialId }: { materialId: number }) => {
		return this.rxShadeSystemRulesService.getDefaultShadeSystemSelectionId({ materialId });
	};

	getIncisalBodyGingivalOptions = ({ materialId, shadeSystemId }: { materialId: number; shadeSystemId: number }): IdName[] => {
		return this.rxShadeSystemRulesService.getIncisalBodyGingivalOptions({ materialId, shadeSystemId }) ?? [];
	};

	getAbutmentOptions(): IdName[] {
		return this.rxRulesService.getAbutmentOptionsMapping();
	}

	getBridgeTeethToUpdate({ teethToUpdate, teethInJaw }: { teethToUpdate: Tooth[]; teethInJaw: Tooth[] }): Tooth[] {
		return this.bridgeService.getBridgeTeethToUpdate({
			teethToUpdate,
			teethInJaw,
			shouldConvertDeletingToregular: this.isProcedureFlow
		});
	}

	validateBridge({ teeth }: { teeth: Tooth[] }): BridgeValidationState {
		if (this.isProcedureFlow) {
			return this.bridgeValidatorService.validateBridgeForProcedureFlow(teeth);
		}

		return this.bridgeValidatorService.validateBridgeForCaseTypeFlow(teeth);
	}

	isPrep = ({ tooth }: { tooth: Tooth }): boolean => {
		const isBridgeTooth = !!tooth.BridgeIndex;

		if (!tooth.UnitTypeID && !tooth.ToothInBridgeTypeID) {
			return false;
		}

		const isPrepTooth = ![
			UnitTypes.Missing_edentulousSpace,
			UnitTypes.Missing_noSpace,
			UnitTypes.Regular,
			UnitTypes.Detachable
		].includes(tooth.UnitTypeID as UnitTypes);
		const isPrepBridgeTooth = ![
			UnitTypesInBridge.Skipped,
			UnitTypesInBridge.Missing,
			UnitTypesInBridge.MarylandAbutment,
			UnitTypesInBridge.Pontic
		].includes(tooth.ToothInBridgeTypeID as UnitTypesInBridge);

		return isBridgeTooth ? isPrepBridgeTooth : isPrepTooth;
	};

	getUnitTypeTranslation({ tooth, isBridgeContext }: { tooth: Tooth; isBridgeContext: boolean }): string {
		const isBridgeTooth = !!tooth.BridgeIndex;

		return this.translateService.instant(
			isBridgeContext
				? `BridgeUnitTypes.${
						UnitTypesInBridge[
							isBridgeTooth
								? (tooth.ToothInBridgeTypeID as any)
								: this.convertToBridgeToothUnitType({ unitType: tooth.UnitTypeID as any })
						]
					}`
				: `UnitTypes.${
						UnitTypes[
							isBridgeTooth
								? this.convertFromBridgeToothUnitType({ unitTypeInBridge: tooth.ToothInBridgeTypeID as any })
								: (tooth.UnitTypeID as any)
						]
					}`
		);
	}

	getToothByToothId({ toothId }: { toothId: number }): Observable<Tooth> {
		return this.teethDiagramQuery.teeth$.pipe(
			map(teeth => {
				const allTeeth: Tooth[] = [...teeth.upperJaw, ...teeth.lowerJaw];

				return allTeeth.find(tooth => tooth.ToothID === toothId);
			}),
			filter((tooth): tooth is Tooth => !!tooth)
		);
	}

	forceUpdateTeeth({ teethToUpdate }: { teethToUpdate: Tooth[] }) {
		this.teethDiagramStore.updateTeeth({ teethToUpdate });
	}

	isPonticMDAbutmentOrMissing(unitType: UnitTypesInBridge): boolean {
		return this.bridgeService.isPonticMDAbutmentOrMissing(unitType);
	}

	isToothMissingOrRegular(unitType: UnitTypes): boolean {
		return [UnitTypes.Missing, UnitTypes.Missing_edentulousSpace, UnitTypes.Missing_noSpace, UnitTypes.Regular].includes(unitType);
	}

	checkIsScanBody({ UnitTypeID, ToothInBridgeTypeID }: Tooth): boolean {
		return (
			!this.isProcedureFlow && (UnitTypeID === UnitTypes.ScanBody || ToothInBridgeTypeID === (UnitTypesInBridge.ScanBody as number))
		);
	}

	checkIsImplantBased({ UnitTypeID, ToothInBridgeTypeID, BridgeIndex }: Tooth): boolean {
		const isImplantBased = BridgeIndex
			? ToothInBridgeTypeID === (UnitTypesInBridge.ImplantBased as number)
			: UnitTypeID === UnitTypes.ImplantBased;

		return this.isProcedureFlow && isImplantBased;
	}

	checkIsImplantPosition({ UnitTypeID }: Tooth): boolean {
		return this.isProcedureFlow && UnitTypeID === UnitTypes.ImplantPosition;
	}

	addHiddenOptionToTop(id: number | string, name: string, optionList: DropDownItem[]): DropDownItem {
		const option = {
			Name: name,
			Id: id,
			Hidden: true
		};

		optionList?.unshift(option);

		return option;
	}
	getSpecificationName(specId: number | string): string {
		const customSpec = this.materialQuery.getSpecificationForSave(specId);

		if (customSpec) {
			return customSpec.name;
		}

		return this.shellQuery.specifications?.find(({ Id }) => Id === specId)?.Name ?? '';
	}

	getMaterialName(materialId: number | string): string {
		const customMaterial = this.materialQuery.getMaterialForSave(materialId);

		if (customMaterial) {
			return customMaterial.name;
		}

		return this.shellQuery.materials?.find(({ Id }) => Id === materialId)?.Name ?? '';
	}

	shouldPreventImplantBasedSelection(unitType: number): boolean {
		return this.implantsViewService.shouldPreventImplantBasedSelection(unitType);
	}

	shouldPreventImplantBasedInBridgeSelection(unitTypeInBridge: UnitTypesInBridge): boolean {
		return this.implantsViewService.shouldPreventImplantBasedInBridgeSelection(unitTypeInBridge);
	}

	private getPreparationDesignName({ preparationDesignId }: { preparationDesignId: Maybe<string> }): string | undefined {
		const preparationDesigns: IdName[] = this.getPreparationDesignOptionsMapping();

		return preparationDesigns?.find(preparationDesignOption => preparationDesignOption.Id === preparationDesignId)?.Name;
	}

	private getMarginDesignName({ marginDesignId }: { marginDesignId: Maybe<string> }): string | undefined {
		const marginDesigns: IdName[] = this.getMarginDesignOptions();

		return marginDesigns?.find(marginDesignOption => marginDesignOption.Id === marginDesignId)?.Name;
	}

	private getProcedureFlowUnitTypeNames(tooth: Tooth): Observable<IdName[]> {
		return this.rxRulesService.getProcedureFlowUnitTypeNames(tooth);
	}

	private getCaseTypeFlowUnitTypeNames(tooth: Tooth): Observable<IdName[]> {
		return this.rxRulesService.getCaseTypeFlowUnitTypeNames(tooth);
	}

	private getProcedureFlowUnitTypeNamesForBridge(tooth: Tooth): Observable<IdName[]> {
		return combineLatest([this.orderQuery.procedureMap$, this.teeth$]).pipe(
			take(1),
			map(([procedureMap, teethInBridge]) => {
				const isOuterTooth = this.bridgeService.isOuterTooth(tooth, teethInBridge);

				return this.rxIndicationRulesService.getProcedureFlowUnitTypeNamesForBridge(procedureMap, tooth, isOuterTooth);
			})
		);
	}

	private getUpdatedTooth({
		tooth,
		crownData,
		abutmentData,
		implantTypeId,
		implantsViewData
	}: {
		tooth: Tooth;
		crownData?: CrownData;
		abutmentData: Maybe<AbutmentData>;
		implantTypeId?: Maybe<number>;
		implantsViewData?: ImplantsViewData;
	}) {
		if (!tooth) {
			return tooth;
		}

		const updatedTooth = tooth;
		const defaultTooth = getNewTooth();

		if (!!crownData) {
			updatedTooth.MaterialID = crownData.material;
			updatedTooth.PreparationDesignBuccalId = crownData.preparationDesignBuccal;
			updatedTooth.PreparationDesignBuccal = this.getPreparationDesignName({
				preparationDesignId: crownData.preparationDesignBuccal
			});
			updatedTooth.PreparationDesignLingualId = crownData.preparationDesignLingual;
			updatedTooth.PreparationDesignLingual = this.getPreparationDesignName({
				preparationDesignId: crownData.preparationDesignLingual
			});
			updatedTooth.MarginDesignBuccalId = crownData.marginDesignBuccal;
			updatedTooth.MarginDesignBuccal = this.getMarginDesignName({ marginDesignId: crownData.marginDesignBuccal });
			updatedTooth.MarginDesignLingualId = crownData.marginDesignLingual;
			updatedTooth.MarginDesignLingual = this.getMarginDesignName({ marginDesignId: crownData.marginDesignLingual });
			updatedTooth.ShadeSystemId = crownData.shadeSystem;
			updatedTooth.ShadeIncisal = crownData.incisal;
			updatedTooth.ShadeBody = crownData.body;
			updatedTooth.ShadeGingival = crownData.gingival;
			updatedTooth.StumpfShade = crownData.stumpShade;
			updatedTooth.SpecificationId = crownData?.specification ?? defaultTooth.SpecificationId;
			updatedTooth.PonticDesignID = crownData.ponticDesign ?? defaultTooth.PonticDesignID;
		}
		if (!!abutmentData) {
			updatedTooth.AbutmentType = abutmentData.abutmentType;
			updatedTooth.AbutmentMaterialId = abutmentData.abutmentMaterial;
			updatedTooth.CAMaterialID = abutmentData.abutmentMaterial;
			updatedTooth.AbutmentMarginStyle = this.getAbutmentMarginStyleName({ marginDesignId: abutmentData.marginStyle });
			updatedTooth.AbutmentMarginStyleId = abutmentData.marginStyle;
			updatedTooth.CAMarginStyle = this.getAbutmentMarginStyleName({ marginDesignId: abutmentData.marginStyle });
			updatedTooth.CAMarginStyleId = abutmentData.marginStyle;
		}
		if (implantTypeId || implantTypeId === 0) {
			updatedTooth.ImplantTypeID = implantTypeId;
		}
		if (implantsViewData) {
			updatedTooth.ImplantsViewData = {
				ImplantManufacturerId: implantsViewData.implantManufacturer?.id,
				ImplantManufacturerName: implantsViewData.implantManufacturer?.name,
				ImplantConnectionId: implantsViewData.implantConnection?.id,
				ImplantConnectionName: implantsViewData.implantConnection?.name,
				ImplantPlatformId: implantsViewData.implantPlatform?.id,
				ImplantPlatformName: implantsViewData.implantPlatform?.name,
				ScanBodyId: implantsViewData.scanBody?.id,
				ScanBodyName: implantsViewData.scanBody?.name,
				PartNumber: implantsViewData.scanBody?.partNumber,
				PartManufacturerName: implantsViewData.scanBody?.partManufacturerName,
				LibrarySuppliers: implantsViewData.scanBody?.librarySuppliers
			};
		}

		return updatedTooth;
	}
}
